import React from 'react';
import { Link } from 'react-router-dom';
import useHeroSlider from '../hooks/useHeroSlider';
import useScrollAnimation from '../hooks/useScrollAnimation';
import ContactForm from '../components/ContactForm';
import TestimonialCarousel from '../components/TestimonialCarousel';
import ListingsCarousel from '../components/ListingsCarousel';
import Loading from '../components/Loading';
import siteContent from '../content';
import useListings from '../hooks/useListings';

const { hero, services, about, testimonials: testimonialsSection, cta, listings } = siteContent.pages.home.sections;
const { testimonials } = siteContent.global;

const Home: React.FC = () => {
  const { currentSlide, goToSlide } = useHeroSlider(hero.slides.length, 5000);
  const { listings: soldListings, isLoading: listingsLoading } = useListings({ type: 'sold', useMockFallback: false });

  // Scroll animations
  const servicesHeader = useScrollAnimation({ threshold: 0 });
  const serviceCard1 = useScrollAnimation({ threshold: 0 });
  const serviceCard2 = useScrollAnimation({ threshold: 0 });
  const serviceCard3 = useScrollAnimation({ threshold: 0 });
  const serviceCard4 = useScrollAnimation({ threshold: 0 });
  const aboutSection = useScrollAnimation({ threshold: 0 });
  const testimonialsHeader = useScrollAnimation({ threshold: 0 });
  const listingsHeader = useScrollAnimation({ threshold: 0 });
  const contactSection = useScrollAnimation({ threshold: 0 });

  return (
    <>
      <title>Kline Real Estate Works | Royersford, PA Real Estate</title>
      <meta
        name="description"
        content="Your trusted real estate team in Royersford, PA. Expert guidance for buyers and sellers in Montgomery County and beyond."
      />
      <meta property="og:title" content="Kline Real Estate Works | Royersford, PA Real Estate" />
      <meta property="og:description" content="Your trusted real estate team in Royersford, PA. Expert guidance for buyers and sellers in Montgomery County and beyond." />
      <meta property="og:image" content="https://keithklinegroup.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content="https://keithklinegroup.com/og-image.jpg" />

      {/* Hero Slider */}
      <div className="hero-slider" data-cms-page="home" data-cms-section="hero">
        {hero.slides.map((slide, index) => (
          <div
            key={index}
            className={`hero hero-slide ${index === currentSlide ? 'active' : ''}`}
            data-cms-field="slides"
            data-cms-index={index}
            data-cms-item-field="image"
            data-cms-type="image"
            style={{
              backgroundImage: `linear-gradient(rgba(0, 43, 92, 0.7), rgba(0, 43, 92, 0.8)), url('${slide.image}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="hero-content">
              <p className="hero-subtitle" data-cms-field="tagline">{hero.tagline}</p>
              <h1
                data-cms-field="slides"
                data-cms-index={index}
                data-cms-item-field="title"
              >
                {slide.title}
              </h1>
              <p
                className="hero-slide-subtitle"
                data-cms-field="slides"
                data-cms-index={index}
                data-cms-item-field="subtitle"
              >
                {slide.subtitle}
              </p>
              <div className="btn-group">
                <Link
                  to={slide.cta.link}
                  className="btn btn-primary btn-lg"
                  data-cms-field="slides"
                  data-cms-index={index}
                  data-cms-item-field="cta.text"
                  data-cms-link-field="cta.link"
                >
                  {slide.cta.text}
                </Link>
                <Link
                  to={hero.contactButton.link}
                  className="btn btn-outline-light btn-lg"
                  data-cms-field="contactButton.text"
                  data-cms-link-field="contactButton.link"
                  data-cms-type="button"
                >
                  {hero.contactButton.text}
                </Link>
              </div>
            </div>
          </div>
        ))}
        {/* Navigation dots */}
        {hero.slides.length > 1 && (
          <div className="hero-dots">
            {hero.slides.map((_, index) => (
              <button
                key={index}
                className={`hero-dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Services Section */}
      <section className="section" data-cms-page="home" data-cms-section="services">
        <div className="container">
          <div
            ref={servicesHeader.ref}
            className={`section-header scroll-fade-up ${servicesHeader.isVisible ? 'visible' : ''}`}
          >
            <h2 data-cms-field="heading">{services.heading}</h2>
            <p data-cms-field="subheading">{services.subheading}</p>
          </div>
          <div className="services-grid" data-cms-list="items">
            {services.items.map((item, index) => {
              const cardRefs = [serviceCard1, serviceCard2, serviceCard3, serviceCard4];
              const cardRef = cardRefs[index];
              return (
                <div
                  key={index}
                  ref={cardRef?.ref}
                  className={`service-card scroll-fade-up stagger-${index + 1} ${cardRef?.isVisible ? 'visible' : ''}`}
                  data-cms-index={index}
                >
                  <span className="material-icons service-icon" data-cms-field="items" data-cms-index={index} data-cms-item-field="icon" data-cms-field-type="icon">{item.icon}</span>
                  <h3 data-cms-field="items" data-cms-index={index} data-cms-item-field="title">{item.title}</h3>
                  <p data-cms-field="items" data-cms-index={index} data-cms-item-field="description">{item.description}</p>
                  <Link to={item.link} className="btn btn-secondary" data-cms-field="items" data-cms-index={index} data-cms-item-field="linkText" data-cms-link-field="link">{item.linkText}</Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section section-bg" data-cms-page="home" data-cms-section="about">
        <div className="container">
          <div
            ref={aboutSection.ref}
            className={`scroll-fade-up ${aboutSection.isVisible ? 'visible' : ''}`}
          >
            <div className="two-column">
              <div>
                <h2 style={{ textAlign: 'center' }} data-cms-field="heading">{about.heading}</h2>
                <div data-cms-list="paragraphs">
                  {about.paragraphs.map((paragraph, index) => (
                    <p key={index} data-cms-field="paragraphs" data-cms-index={index}>
                      {paragraph}
                    </p>
                  ))}
                </div>
                <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                  <Link to={about.ctaLink} className="btn btn-primary" data-cms-field="ctaText" data-cms-link-field="ctaLink">{about.ctaText}</Link>
                </div>
              </div>
              <div style={{ textAlign: 'center', position: 'relative' }}>
                <img
                  src={about.image}
                  alt="Keith Kline"
                  data-cms-field="image"
                  data-cms-type="image"
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    maxWidth: '400px',
                    width: '100%',
                    display: 'block',
                    margin: '0 auto'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section" data-cms-page="home" data-cms-section="testimonials" style={{ paddingBottom: testimonials.length > 3 ? 'calc(var(--spacing-3xl) + 2rem)' : undefined }}>
        <div className="container">
          <div
            ref={testimonialsHeader.ref}
            className={`section-header scroll-fade-up ${testimonialsHeader.isVisible ? 'visible' : ''}`}
          >
            <h2 data-cms-field="heading">{testimonialsSection.heading}</h2>
            <p data-cms-field="subheading">{testimonialsSection.subheading}</p>
          </div>
          <TestimonialCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-navy" data-cms-page="home" data-cms-section="cta">
        <div className="container">
          <div className="section-header">
            <h2 data-cms-field="heading">{cta.heading}</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)' }} data-cms-field="subheading">
              {cta.subheading}
            </p>
            <div style={{ marginTop: '2rem' }}>
              <Link to={cta.ctaLink} className="btn btn-primary btn-lg" data-cms-field="ctaText" data-cms-link-field="ctaLink">{cta.ctaText}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Recently Sold Section */}
      <section className="section section-bg" data-cms-page="home" data-cms-section="listings">
        <div className="container">
          <div
            ref={listingsHeader.ref}
            className={`section-header scroll-fade-up ${listingsHeader.isVisible ? 'visible' : ''}`}
          >
            <h2 data-cms-field="heading">{listings.heading}</h2>
            <p data-cms-field="subheading">{listings.subheading}</p>
          </div>
          {listingsLoading ? (
            <Loading message="Loading recent sales..." />
          ) : (
            <ListingsCarousel listings={soldListings} limit={10} />
          )}
          <div className="text-center mt-4">
            <Link
              to={listings.ctaLink}
              className="btn btn-secondary btn-lg"
              data-cms-field="ctaText"
              data-cms-link-field="ctaLink"
              data-cms-type="button"
            >
              {listings.ctaText}
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section">
        <div className="container">
          <div
            ref={contactSection.ref}
            className={`scroll-scale ${contactSection.isVisible ? 'visible' : ''}`}
            style={{ maxWidth: '700px', margin: '0 auto' }}
          >
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
